import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import isEmpty from 'lodash-es/isEmpty';

import {
    OneIdResponse,
    OneIdNewsletter,
    OneIdNewsletterMarketingEntry,
    OneIdNewsletterLegalEntry,
    OneIdNewsletterRequest,
    Guest,
    OneIdOptions,
    OneIdInstance
} from './one-id.interface';
import { ONE_ID_INSTANCE } from '@finder/shared/services/one-id/one-id.factory';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';

export type OneIdEvents
    = 'age-gated' | 'close' | 'create' | 'error' | 'init' | 'login' | 'logout' | 'low-trust'
    | 'opt-in' | 'primary-account' | 'reauth' | 'refresh' | 'update';

const NEWSLETTER_SUBSCRIPTION_STATUS_COOKIE = 'newslettersSubscriptionStatus';

@Injectable()
export class OneIdService {
    private newslettersCache: OneIdResponse<OneIdNewsletter>;

    constructor(
        @Inject(ONE_ID_INSTANCE) private oid: Partial<OneIdInstance>,
        private debugLog: DebugLogService,
    ) {}

    getGuest(): Promise<OneIdResponse<Guest>> {
        return this.oid.getGuest();
    }

    getGuestNewsletters(): Promise<OneIdResponse<OneIdNewsletter[]>> {
        return this.oid.getGuestNewsletters();
    }

    optIn(email, campaignId, options: OneIdOptions = {}) {
        return this.getInlineNewsletters(campaignId, options)
            .then((response: OneIdResponse<OneIdNewsletter>) => {
                const payload = this.buildInlineNewsletterRequest(campaignId, email, response);
                this.setInlineNewsletters(payload, options);

                return response;
            });
    }

    /* istanbul ignore next */
    on(event: OneIdEvents, listener: () => void) {
        this.oid.on(event, listener);
    }

    /* istanbul ignore next */
    off(event: OneIdEvents, listener?: () => void) {
        this.oid.off(event, listener);
    }

    /**
     * Retrieves a list of configured newsletter campaigns; additionally, legal guidelines are provided if required.
     * This method returns a promise, that, when fulfilled, provides the integrator with data needed to render
     * a newsletter campaign to a user.
     */
    getInlineNewsletters(campaignId, options = {}, forceRefresh = false): Promise<OneIdResponse<OneIdNewsletter>> {
        return new Promise((resolve, reject) => {
            /* istanbul ignore if */
            if (!forceRefresh && this.newslettersCache) {
                resolve(this.newslettersCache);
            }

            this.oid?.getInlineNewsletters(campaignId, options)
                .then((response: OneIdResponse<OneIdNewsletter>) => {
                    this.newslettersCache = response;
                    resolve(response);
                });
        });
    }

    launchNewsletters(campaignId, options: any = {}): Promise<void> {
        // Make sure the options are formatted properly
        let optionalConfig;
        if (!isEmpty(options) && !options.optionalConfig) {
            optionalConfig = {
                optionalConfigs: {
                    reporting: options,
                }
            };
        } else {
            optionalConfig = options;
        }

        return this.oid.launchNewsletters(campaignId, optionalConfig);
    }

    getCookieKey(campaignId = 'unknown'): string {
        return `${NEWSLETTER_SUBSCRIPTION_STATUS_COOKIE}-${campaignId}`;
    }

    // TODO - Add logic here that calls a different endpoint for GDPR/EMEA
    // I cannot do this right now because OneID is not working
    signUp(campaignId, options: any = {}): Promise<void> {
        this.debugLog.info('***** oid', this.oid);

        return this.launchNewsletters(campaignId, options);
    }

    // eslint-disable-next-line max-len
    private buildInlineNewsletterRequest(campaignId, email, response: OneIdResponse<OneIdNewsletter>): OneIdNewsletterRequest {
        const marketing = response.data.marketing.map((entry: OneIdNewsletterMarketingEntry) => ({
            code: entry.code,
            subscribed: true
        }));

        const legal = response.data.legal.map((entry: OneIdNewsletterLegalEntry) => ({
            code: entry.code,
            accepted: true
        }));

        return {
            campaignId,
            email,
            marketing,
            legal,
        };
    }

    private setInlineNewsletters(data: OneIdNewsletterRequest, options: OneIdOptions) {
        return this.oid.setInlineNewsletters(data, options);
    }
}
